import { driverScreens } from "./navigation.ts";

/**
 * Which screen the address bar is asking for.
 *
 * A query parameter rather than a path, for the reason the dispatch console arrived at: a path needs
 * the server to answer every path with the application's document, and these channels are served as
 * static files from a gateway nobody here configures. `/?page=offer` is one document at one path and
 * needs nothing.
 *
 * It matters more here than it does on a console, for two reasons that have nothing to do with
 * bookmarks. Signing in leaves this origin for Keycloak and comes back, so the open screen has to be
 * written somewhere that survives the round trip. And the acceptance check has to be able to put
 * this application on one named screen and compare it against one named design state — which is the
 * whole of `CLAUDE.md`'s step 1, "put it in the same state".
 */

export const PAGE_PARAMETER = "page";

/**
 * Where a courier lands with nothing asked for.
 *
 * The first screen of the flow, which is the readiness screen, because every shift starts there:
 * the design's own first screen is `Ready to go online?` and the three screens after it are
 * unreachable until it passes.
 */
export const DEFAULT_PAGE_ID = "go-online";

/**
 * Every identifier the router has a branch for.
 *
 * Deliberately the **built** screens rather than all eighteen. An address naming a designed screen
 * this build does not draw must reach the page that says what it is waiting on, not be silently
 * redirected to the readiness screen as though it had been a typo — the two are different facts and
 * a courier told the wrong one would conclude the screen does not exist.
 */
const BUILT: ReadonlySet<string> = new Set(
  driverScreens.filter((screen) => screen.built).map((screen) => screen.id),
);

/** Every identifier either built or designed, so a designed-but-unbuilt address is recognised. */
const DESIGNED: ReadonlySet<string> = new Set(driverScreens.map((screen) => screen.id));

/**
 * Read the screen out of a URL.
 *
 * An unrecognised identifier falls back to the default, which is the stale-bookmark case. A
 * recognised one that is not built is returned as it stands, so the router can say what that screen
 * needs.
 */
export function pageIdIn(url: string): string {
  const asked = new URL(url).searchParams.get(PAGE_PARAMETER);
  if (asked === null) return DEFAULT_PAGE_ID;
  if (BUILT.has(asked) || DESIGNED.has(asked)) return asked;
  return DEFAULT_PAGE_ID;
}

/**
 * The same URL, asking for a different screen.
 *
 * Everything else in the address is preserved, which matters because this is also the URL the
 * sign-in flow stores as its return address: dropping a parameter here would silently drop it from
 * what a courier comes back to.
 */
export function urlForPage(url: string, pageId: string): string {
  const next = new URL(url);
  if (pageId === DEFAULT_PAGE_ID) next.searchParams.delete(PAGE_PARAMETER);
  else next.searchParams.set(PAGE_PARAMETER, pageId);
  return next.toString();
}
